import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { cascadeInto, createNextMonth } from './rollover'
import { stepMonth } from './format'
import { fetchAllBudgetMonths, fetchBudgetMonth, saveBudgetMonth } from './repository'
import type { BudgetMonth } from './types'

/**
 * After a past month's assignments change, walk forward through every
 * existing later month re-deriving carryOvers. Fire-and-forget from
 * mutation onSettled; bounded by how many month docs exist.
 */
async function cascadeForward(qc: QueryClient, year: number, month: number): Promise<void> {
  let cur =
    qc.getQueryData<BudgetMonth | null>(['budget', year, month]) ??
    (await fetchBudgetMonth(year, month))
  while (cur) {
    const step = stepMonth(cur.year, cur.month, 1)
    const next = await fetchBudgetMonth(step.year, step.month)
    if (!next) return
    const updated = cascadeInto(cur, next)
    await saveBudgetMonth(updated)
    qc.setQueryData(['budget', step.year, step.month], updated)
    cur = updated
  }
}

/**
 * On-load catch-up (mirrors iOS MonthRolloverRunner.run): from the
 * latest month <= today, create each missing month up to today.
 * Returns how many months were created.
 */
async function runMonthRollover(now = new Date()): Promise<number> {
  const all = await fetchAllBudgetMonths()
  if (all.length === 0) return 0
  const todayY = now.getFullYear()
  const todayM = now.getMonth() + 1
  const key = (y: number, m: number) => y * 12 + m
  const sorted = [...all].sort((a, b) => key(a.year, a.month) - key(b.year, b.month))
  let current = [...sorted].reverse().find((m) => key(m.year, m.month) <= key(todayY, todayM))
  if (!current) return 0

  let created = 0
  while (key(current.year, current.month) < key(todayY, todayM)) {
    const step = stepMonth(current.year, current.month, 1)
    const existing = sorted.find((m) => m.year === step.year && m.month === step.month)
    if (existing) {
      current = existing
      continue
    }
    const rolled = createNextMonth(current, step.year, step.month)
    await saveBudgetMonth(rolled)
    current = rolled
    created += 1
  }
  return created
}

/** Post-mutation hook point: refetch the edited month, then re-derive
 *  every later month's carryOver so future TBAs don't go stale. */
export function afterMonthMutation(qc: QueryClient, year: number, month: number): void {
  void qc.invalidateQueries({ queryKey: ['budget', year, month] })
  cascadeForward(qc, year, month).catch((e) => console.warn('carryOver cascade failed:', e))
}

/** Once per mount: create any missing months up to today. Mirrors
 *  iOS MonthRolloverRunner running on launch. */
export function useRolloverCatchUp() {
  const qc = useQueryClient()
  const ran = useRef(false)
  useEffect(() => {
    if (ran.current) return
    ran.current = true
    runMonthRollover()
      .then((created) => {
        if (created > 0) void qc.invalidateQueries({ queryKey: ['budget'] })
      })
      .catch((e) => console.warn('month rollover catch-up failed:', e))
  }, [qc])
}

interface CreateNextArgs {
  from: BudgetMonth
  year: number
  month: number
}

/** "Start <month> from <prior>" — budgeting ahead into a month that
 *  doesn't exist yet. */
export function useCreateNextMonth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: CreateNextArgs) => {
      const m = createNextMonth(args.from, args.year, args.month)
      await saveBudgetMonth(m)
      return m
    },
    onSuccess: (m, args) => {
      qc.setQueryData(['budget', args.year, args.month], m)
    },
  })
}
